import { TenantOrderLine } from '~/lib/model';
import { Big } from 'big.js';
import TwtJob from '~/tenants/twt/model/TwtJob';
import { schema_latest_version, ZodVersionedMetadata } from '~/lib/zod';
import TwtSocialVerticalConfig from '~/tenants/twt/performable/social-vertical/TwtSocialVerticalConfig';
import TwtSlideshowConfig from '~/tenants/twt/performable/slideshow/TwtSlideshowConfig';
import TwtAiReelConfig from '~/tenants/twt/performable/ai-reel/TwtAiReelConfig';
import { DeliverableType } from '~common/model/Deliverable';

/**
 * What the vertical social video costs at order time: the reel's price after
 * delivery (Twist's catalog product, .server/reel-upsell.ts), so the same
 * video is never two prices. Taxed, as that line is (founder, 14 Oct 2026),
 * and as the Social Media Slideshow is, so the $16 and the $19 upgrade come to
 * the slideshow's $35 with its tax, either way round.
 */
export const TWT_SOCIAL_VERTICAL_PRICE = 16;

/**
 * THE VERTICAL SOCIAL VIDEO, ORDERED WITH THE ORDER (founder, 14 Oct 2026).
 *
 * The same deliverable as the reel bought after delivery: TwtOrder.slideshows
 * turns `vertical` on for it, and the order's slideshow pass builds the
 * branded 9:16 with the rest.
 *
 * A JOB here, where the post-delivery reel is a stamp. That one is a stamp
 * because adding a job to a DELIVERED order reopens it and re-runs delivery.
 * At order time nothing has been delivered, and a job is what every surface
 * that places an order already understands: the wizard's Add card, the quick
 * form's catalog product (`required_services: ['social-vertical']`), the
 * invoice line, the office's job list.
 *
 *   - Generated (deliverable SLIDESHOW): no provider, no visit, no upload,
 *     no payout (no expense lines). Its one step completes when the order's
 *     slideshows have rendered (JobEntity), and the order's own completion
 *     never waits on it (OrderEntity.workCompleted).
 *   - Registered AFTER the Social Media Slideshow: TenantWrapper.slideshow()
 *     takes the first SLIDESHOW performable for "Unlock Slideshows".
 *   - Not offered with the Social Media Slideshow, which includes it, nor with
 *     the AI reel, which replaces the order's slideshows altogether. Added
 *     first and then the slideshow, the slideshow is priced as the upgrade
 *     ($35 less this, TwtSlideshowJob), so the two never cost more than the
 *     slideshow alone.
 *   - Never offered after delivery as well: with `vertical` on, the reel offer
 *     says there is nothing to sell (reelEligibility).
 */
export class TwtSocialVerticalJob extends TwtJob<typeof TwtSocialVerticalConfig> {
  get performable(): typeof TwtSocialVerticalConfig {
    return TwtSocialVerticalConfig;
  }

  get deliverable(): DeliverableType {
    return DeliverableType.SLIDESHOW;
  }

  get short(): string {
    return 'VERT';
  }

  /**
   * Offered only where it can be made and is not had already:
   *
   *   - the buyer has an agent profile (any, as slideshowProfileIds counts).
   *     The 9:16 is branded-only, and with no branding it is never built: an
   *     agent with none was sold $16 of nothing (review of #461). Hidden for
   *     them rather than shown greyed out; the Deliverables page asks for the
   *     branding and sells the reel after delivery (reel-panel.ts);
   *   - photos to make it from;
   *   - neither the Social Media Slideshow (which includes it) nor the AI reel
   *     (which replaces the order's slideshows), nor a reel already bought.
   *
   * Anything that slips past this (the office adds jobs from the full list) is
   * caught at delivery: what cannot be made comes off (social-vertical.ts).
   */
  get eligible() {
    const photos = this.order.jobs.some((job) => job.performable_id === 'photo' || job.performable_id === 'byop');

    return (
      this.order.buyer.branded === true &&
      this.order.reelPaid !== true &&
      photos &&
      !this.order.jobs.some(
        (job) => job.performable_id === TwtSlideshowConfig.id || job.performable_id === TwtAiReelConfig.id,
      )
    );
  }

  defaultValue(): ZodVersionedMetadata<(typeof TwtSocialVerticalConfig)['schema']> {
    return {
      version: schema_latest_version(TwtSocialVerticalConfig.schema),
    };
  }

  revenueLines(): TenantOrderLine[] {
    const lines = super.revenueLines();

    // It could not be made and was taken off (social-vertical.ts): nothing to charge.
    if (this.order.socialVerticalRefunded) {
      return lines;
    }

    lines.push({
      amount: new Big(TWT_SOCIAL_VERTICAL_PRICE),
      taxable: true,
      description: 'Vertical Social Video (9:16)',
      id: 'social_vertical',
      discountable: false,
    });

    return lines;
  }
}
